/**
 * Microphone permission + device selection for voice notes.
 *
 * The browser owns the permission prompt: the first `getUserMedia` call shows
 * it and a denial cannot be re-prompted from code. Device labels and stable
 * ids are only exposed once permission is granted, so the panel offers a
 * "Set up microphone" step (`requestAccess`) before the first recording. The
 * chosen device is remembered per browser in localStorage (id + label, since
 * Safari rotates ids between sessions) and falls back to the system default
 * when it disappears. `devicechange` refreshes the list when a USB mic is
 * plugged in or a Bluetooth headset connects.
 */
import {
  type AudioInputDevice,
  describeMicError,
  MIC_DEVICE_ID_STORAGE_KEY,
  MIC_DEVICE_LABEL_STORAGE_KEY,
  type MicPermissionState,
  permissionStateFromError,
  pickAudioInputDevice,
  toAudioInputDevices
} from "@frontend/pages/case-workspace/pathology/lib/audio-input-devices"
import {
  buildAudioConstraints,
  DEAD_INPUT_CHECK_MS,
  watchDeadInput
} from "@frontend/pages/case-workspace/pathology/lib/voice-note-recording"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"

/** The preview must outlive the dead-input check, which a stopped stream cancels. */
const PREVIEW_AUTO_STOP_MS = DEAD_INPUT_CHECK_MS + 2_000

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private mode / blocked storage: the choice simply does not persist.
  }
}

function stopStream(stream: MediaStream | null): void {
  if (!stream) return
  for (const track of stream.getTracks()) track.stop()
}

export interface AudioInputDevicesState {
  devices: AudioInputDevice[]
  /** `null` = system default. */
  selectedDeviceId: string | null
  setSelectedDeviceId: (deviceId: string | null) => void
  permissionState: MicPermissionState
  /** Prompts for microphone access (if needed) and refreshes the device list. */
  requestAccess: () => Promise<boolean>
  /** Open the selected microphone for a few seconds so the level meter shows it is live. */
  previewStream: MediaStream | null
  startPreview: () => Promise<void>
  stopPreview: () => void
}

export function useAudioInputDevices({ enabled }: { enabled: boolean }): AudioInputDevicesState {
  const [devices, setDevices] = useState<AudioInputDevice[]>([])
  const [permissionState, setPermissionState] = useState<MicPermissionState>("unknown")
  const [selectedDeviceId, setSelectedDeviceIdState] = useState<string | null>(() =>
    readStorage(MIC_DEVICE_ID_STORAGE_KEY)
  )
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null)
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previewDeadWatchStopRef = useRef<(() => void) | null>(null)
  const selectedDeviceIdRef = useRef(selectedDeviceId)

  useEffect(() => {
    selectedDeviceIdRef.current = selectedDeviceId
  }, [selectedDeviceId])

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return
    try {
      const list = toAudioInputDevices(await navigator.mediaDevices.enumerateDevices())
      setDevices(list)
      // Re-resolve the remembered choice against what is actually plugged in.
      const resolved = pickAudioInputDevice(
        list,
        readStorage(MIC_DEVICE_ID_STORAGE_KEY),
        readStorage(MIC_DEVICE_LABEL_STORAGE_KEY)
      )
      if (resolved !== selectedDeviceIdRef.current) {
        setSelectedDeviceIdState(resolved)
      }
    } catch {
      setDevices([])
    }
  }, [])

  // Permission state (Chrome / Edge expose it; Firefox and Safari throw → "unknown").
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus | null>(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    void (async () => {
      try {
        const status = await navigator.permissions.query({ name: "microphone" as PermissionName })
        if (cancelled) return
        setPermissionStatus(status)
        setPermissionState(status.state as MicPermissionState)
      } catch {
        // Not supported: stays "unknown" until getUserMedia tells us.
      }
      if (!cancelled) await refreshDevices()
    })()
    return () => {
      cancelled = true
    }
  }, [enabled, refreshDevices])

  // Follow later grants / revocations made in the browser's site settings.
  useEffect(() => {
    if (!permissionStatus) return
    const onChange = () => {
      setPermissionState(permissionStatus.state as MicPermissionState)
      if (permissionStatus.state === "granted") void refreshDevices()
    }
    permissionStatus.addEventListener("change", onChange)
    return () => permissionStatus.removeEventListener("change", onChange)
  }, [permissionStatus, refreshDevices])

  // Hot-plug: USB mics, Bluetooth headsets.
  useEffect(() => {
    if (!(enabled && navigator.mediaDevices?.addEventListener)) return
    const onDeviceChange = () => void refreshDevices()
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange)
    return () => navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange)
  }, [enabled, refreshDevices])

  const setSelectedDeviceId = useCallback(
    (deviceId: string | null) => {
      setSelectedDeviceIdState(deviceId)
      writeStorage(MIC_DEVICE_ID_STORAGE_KEY, deviceId)
      writeStorage(MIC_DEVICE_LABEL_STORAGE_KEY, devices.find((d) => d.deviceId === deviceId)?.label ?? null)
    },
    [devices]
  )

  const requestAccess = useCallback(async (): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser cannot access the microphone.")
      return false
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stopStream(stream)
      setPermissionState("granted")
      await refreshDevices()
      return true
    } catch (error) {
      setPermissionState(permissionStateFromError(error))
      toast.error("Microphone unavailable", { description: describeMicError(error) })
      return false
    }
  }, [refreshDevices])

  const stopPreview = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
    previewDeadWatchStopRef.current?.()
    previewDeadWatchStopRef.current = null
    setPreviewStream((current) => {
      stopStream(current)
      return null
    })
  }, [])

  const startPreview = useCallback(async () => {
    stopPreview()
    try {
      const stream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(selectedDeviceIdRef.current))
      setPermissionState("granted")
      setPreviewStream(stream)
      previewDeadWatchStopRef.current = watchDeadInput(stream)
      previewTimerRef.current = setTimeout(stopPreview, PREVIEW_AUTO_STOP_MS)
      void refreshDevices()
    } catch (error) {
      setPermissionState(permissionStateFromError(error))
      toast.error("Microphone unavailable", { description: describeMicError(error) })
    }
  }, [refreshDevices, stopPreview])

  // Never leave a preview microphone open when the panel goes away.
  useEffect(() => () => stopPreview(), [stopPreview])

  return {
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    permissionState,
    requestAccess,
    previewStream,
    startPreview,
    stopPreview
  }
}

/** 0..1 RMS level of a live stream, sampled every animation frame. */
export function useAudioLevelMeter(stream: MediaStream | null): number {
  const [level, setLevel] = useState(0)

  useEffect(() => {
    if (!stream || typeof AudioContext === "undefined") {
      setLevel(0)
      return
    }
    const context = new AudioContext()
    const source = context.createMediaStreamSource(stream)
    const analyser = context.createAnalyser()
    analyser.fftSize = 512
    source.connect(analyser)
    const samples = new Uint8Array(analyser.fftSize)
    let raf = 0
    let lastUpdate = 0
    const tick = (now: number) => {
      analyser.getByteTimeDomainData(samples)
      let sum = 0
      for (let i = 0; i < samples.length; i++) {
        const v = ((samples[i] as number) - 128) / 128
        sum += v * v
      }
      const rms = Math.sqrt(sum / samples.length)
      // ~15 updates/s is plenty for a level bar and avoids re-rendering the panel every frame.
      if (now - lastUpdate > 66) {
        lastUpdate = now
        setLevel(Math.min(1, rms * 3))
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      source.disconnect()
      void context.close()
      setLevel(0)
    }
  }, [stream])

  return level
}
